// The Pro Studio redesign's pages (spec ui-perf/pro-studio-redesign), beside the current ones:
// every page is under the brand's `home2`, which draws the redesign's own frame.
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";

import { brandRoute, teamRoute } from "../../platform/routes";
import { ActorsPage } from "./actors/ActorsPage";
import { BrandsPage } from "./brands/BrandsPage";
import { CreateImageScreen } from "./create/CreateImageScreen";
import { CreateSoundScreen } from "./create/CreateSoundScreen";
import { CreateTalkingScreen } from "./create/CreateTalkingScreen";
import { CreateVideoScreen } from "./create/CreateVideoScreen";
import { GenerationsPage } from "./generations/GenerationsPage";
import { Home2Screen } from "./Home2Screen";
import { ProNotFound } from "./ProNotFound";
import { LibraryPage } from "./library/LibraryPage";
import { ProductsPage } from "./products/ProductsPage";
import { Studio2Entry } from "./screens";
import { SettingsPage } from "./settings/SettingsPage";
import { Studio2Shell } from "./Studio2Shell";

/**
 * `/teams/$teamId/home2`: the way in, to the team's first brand's Home 2. It has no frame of its
 * own: its "Add a brand first" and its errors show in the current shell, with its account menu.
 */
export const studio2EntryRoute = createRoute({
  getParentRoute: () => teamRoute,
  path: "home2",
  component: Studio2Entry,
});

export const studio2Route = createRoute({
  getParentRoute: () => brandRoute,
  path: "home2",
  staticData: { ownFrame: true },
  component: Studio2Shell,
});

// `home2/<anything>` with no page: Pro's own not-found, in Pro's frame. Without it the signed-in
// layout's `$` takes the address and draws the root's (spec ui-perf/pro-fixes-notfound-case).
export const studio2UnknownRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "$",
  component: ProNotFound,
});

export const home2Route = createRoute({
  getParentRoute: () => studio2Route,
  path: "/",
  component: Home2Screen,
});

// Simple's route is composed in `app/pro-simple.tsx`: its ad step draws the templates area's forms.

// `?mode=ad-static`: the prototype's Ad static (with ad copy); anything else is Image.
const createImageSearch = z.object({
  mode: z.enum(["image", "ad-static"]).optional().catch(undefined),
});

export const createImageRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "create-image",
  validateSearch: (search) => createImageSearch.parse(search),
  component: CreateImageScreen,
});

export const createVideoRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "create-video",
  component: CreateVideoScreen,
});

export const createTalkingRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "create-talking",
  component: CreateTalkingScreen,
});

export const createSoundRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "create-sound",
  component: CreateSoundScreen,
});

// `?tab=`: the prototype's Your team's actors or Make an actor; anything else is Global actors.
const actorsSearch = z.object({
  tab: z.enum(["team", "make"]).optional().catch(undefined),
});

export const studio2ActorsRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "actors",
  validateSearch: (search) => actorsSearch.parse(search),
  component: ActorsPage,
});

// `?kind=`: one of the prototype's kinds; anything else is All. `?job=`: a job whose detail opens
// over the page (Simple's Open in Pro, spec ui-perf/simple-studio); anything else is none.
const generationsSearch = z.object({
  kind: z.enum(["image", "ad-static", "video", "audio"]).optional().catch(undefined),
  job: z.string().uuid().optional().catch(undefined),
});

export const studio2GenerationsRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "generations",
  validateSearch: (search) => generationsSearch.parse(search),
  component: GenerationsPage,
});

// Templates' route is composed in `app/pro-templates.tsx`: it joins the templates area's pieces.

// `?tab=`: the prototype's Uploads, Prompt templates or Voices (spec ui-perf/pro-library-tabs);
// anything else is Projects.
const librarySearch = z.object({
  tab: z.enum(["uploads", "prompt-templates", "voices"]).optional().catch(undefined),
});

export const studio2LibraryRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "library",
  validateSearch: (search) => librarySearch.parse(search),
  component: LibraryPage,
});

export const studio2ProductsRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "products",
  component: ProductsPage,
});

export const studio2BrandsRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "brands",
  component: BrandsPage,
});

export const studio2SettingsRoute = createRoute({
  getParentRoute: () => studio2Route,
  path: "settings",
  component: SettingsPage,
});
